import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  /**
   * Keep this route out of the index.
   *
   * For pages that exist but should never earn an impression: the apply/intake
   * redirects, and the 404. `/apply` currently draws 164 impressions at
   * position 21 and converts none of them — it is a redirect, not a page, and
   * every impression it takes is one the real pages could have had.
   */
  noindex?: boolean;
}

const SEO = ({
  title = 'Commissary Connect - Commercial Kitchen Space Vancouver',
  description = 'Vancouver\'s premier shared-use commercial kitchen facilities. Purpose-built for food trucks, caterers, meal prep, bakeries & CPG brands. 5 BC locations.',
  keywords = 'commercial kitchen Vancouver, commissary kitchen, shared kitchen space, food truck kitchen, ghost kitchen Vancouver',
  noindex = false,
}: SEOProps) => {
  useEffect(() => {
    // Update document title
    document.title = title;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords);
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', title);
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', description);
    }
    
    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', title);
    }
    
    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', description);
    }
    // Canonical. Every route previously inherited whatever index.html carried,
    // so Google saw the same canonical on every page.
    const href = `https://commissaryconnect.com${window.location.pathname}`.replace(/\/$/, '') || 'https://commissaryconnect.com/';
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = href;

    // Keep the share-preview URLs on the same page, not the homepage.
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', href);
    document.querySelector('meta[property="twitter:url"]')?.setAttribute('content', href);

    // robots. This is a single-page app, so the tag persists across client-side
    // navigation — it MUST be removed again when a normal page mounts, or one
    // visit to /apply would de-index every page the visitor browsed afterwards.
    //
    // ⚠️ INVARIANT: every route rendered by App.tsx mounts <SEO />. The removal
    // below is the ONLY thing that clears a noindex, so a route that skipped
    // SEO would silently inherit the robots tag from whatever the visitor
    // looked at previously — along with its title, description and canonical.
    // If you add a route, add SEO to it. Verified for all nine current routes.
    //
    // `robots` is the ONLY crawler directive on the page: Googlebot obeys it, so the shell carries
    // no `googlebot` tag (CMSY-145). Do not add one back without keeping it in step here — the
    // shell's static googlebot "index, follow" sat beside every "noindex, follow" until then.
    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (noindex) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.name = 'robots';
        document.head.appendChild(robots);
      }
      robots.content = 'noindex, follow';
      // A noindex page must not also claim to be canonical for itself —
      // that is a contradictory pair of signals.
      link.remove();
    } else if (robots) {
      robots.remove();
    }
  }, [title, description, keywords, noindex]);
  
  return null;
};

export default SEO;